/**
 * Ported verbatim from ai4swe-secops-app (src/utils/yamlBuilder.ts).
 * Builds YAML content from structured onboarding form data.
 * No external YAML library needed — the structure is fixed.
 */

export interface SpocEntry {
  name: string;
  email: string;
}

export interface GheRepo {
  url: string;
  branch: string;
  languages: string[];
  frameworks: string[];
  buildTools: string[];
  /** Per-repository criticality — distinct from the project-level classification.criticality field. */
  criticality: 'HIGH' | 'MEDIUM' | 'LOW' | '';
  /** Whether this repository backs infrastructure or a non-infra (application) codebase. */
  type: 'INFRA' | 'NON_INFRA' | '';
}

export interface MemberEntry {
  email: string;
  role: 'member' | 'admin';
}

export interface ManagementAccessEntry {
  email: string;
  managementLevel: string;
  managerEmail?: string;
}

export interface OnboardingFormData {
  // Project
  projectName: string;
  leanIxId: string;
  projectPhase: string;
  spocs: SpocEntry[];
  // Classification
  criticality: string;
  dataClassification: string;
  complianceTags: string[];
  // Tokens
  ghePat: string;
  sonarToken: string;
  blackduckApiToken: string;
  trivyRobotSecret: string;
  // Connections — GHE
  gheRepos: GheRepo[];
  // Connections — Sonar
  sonarUrl: string;
  sonarOrg: string;
  // Connections — BlackDuck
  blackduckUrl: string;
  // Connections — Trivy
  trivyUsername: string;
  // Connections — Container / Image Scan
  containerRegistry: string;
  containerProject: string;
  containerImages: string[];
  containerSeverityThreshold: string[];
  containerScanSchedule: string;
  // Connections — Polaris (application UUID only; no credential — Sentinel's service
  // account is centrally managed, see docs/POLARIS_ONBOARDING.md)
  polarisApplicationId: string;
  // Connections — Gitleaks
  gitleaksEnabled: boolean;
  gitleaksConfigPath: string;
  // Tech stack
  languages: string[];
  frameworks: string[];
  buildTools: string[];
  // Membership
  members: MemberEntry[];
  // Management Access
  managementAccess: ManagementAccessEntry[];
  // Notifications (readonly, auto-derived)
  notificationEmails: string[];
  alertTypes: string[];
}

export const EMPTY_FORM: OnboardingFormData = {
  projectName: '',
  leanIxId: '',
  projectPhase: 'PLANNING',
  spocs: [{ name: '', email: '' }],
  criticality: 'MEDIUM',
  dataClassification: 'INTERNAL',
  complianceTags: [],
  ghePat: '',
  sonarToken: '',
  blackduckApiToken: '',
  trivyRobotSecret: '',
  gheRepos: [{ url: '', branch: '', languages: [], frameworks: [], buildTools: [], criticality: '', type: '' }],
  sonarUrl: 'https://sonarcloud.io',
  sonarOrg: '',
  blackduckUrl: 'https://mercedes-benz.app.blackduck.com',
  trivyUsername: '',
  containerRegistry: '',
  containerProject: '',
  containerImages: [],
  containerSeverityThreshold: ['BLOCKER', 'CRITICAL'],
  containerScanSchedule: 'on_push',
  polarisApplicationId: '',
  gitleaksEnabled: true,
  gitleaksConfigPath: '.gitleaks.toml',
  languages: [],
  frameworks: [],
  buildTools: [],
  members: [],
  managementAccess: [],
  notificationEmails: [],
  alertTypes: ['CVE_CRITICAL', 'SECRET_LEAK'],
};

/** Collect all unique non-empty emails entered across the form. */
export function deriveNotificationEmails(d: OnboardingFormData): string[] {
  const emails = new Set<string>();
  d.members.forEach((m) => { if (m.email) emails.add(m.email); });
  return Array.from(emails);
}

/** Derive a config_name from the project name. */
export function deriveConfigName(d: OnboardingFormData): string {
  return d.projectName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'unnamed-config';
}

const indent = (level: number) => '  '.repeat(level);
const VALID_PROJECT_PHASES = ['PLANNING', 'ACTIVE_DEVELOPMENT', 'KTLO', 'MAINTENANCE', 'SUNSET'] as const;
const quote = (s: string) => `"${s}"`;
const listStr = (arr: string[]) => `[${arr.map(quote).join(', ')}]`;

function normalizePhase(v: string): string {
  const upper = v?.trim().toUpperCase();
  return VALID_PROJECT_PHASES.includes(upper as typeof VALID_PROJECT_PHASES[number]) ? upper : 'PLANNING';
}

function getRepoNameFromUrl(url: string): string {
  const match = url.match(/\/([^/]+)\/([^/]+?)(?:\.git)?$/);
  return match ? `${match[1]}/${match[2]}` : '';
}

export function buildYaml(d: OnboardingFormData): string {
  const deptId = 'DEPT-001';
  const cocId = 'COC-001';

  const lines: string[] = [
    'version: "1.0"',
    '',
    '# Organisation Tokens (inherited by all projects)',
    'org_tokens:',
    `${indent(1)}ghe_pat: ${quote(d.ghePat || '${GHE_PAT}')}`,
    `${indent(1)}sonar_token: ${quote(d.sonarToken || '${SONAR_TOKEN}')}`,
    `${indent(1)}blackduck_api_token: ${quote(d.blackduckApiToken || '${BLACKDUCK_API_TOKEN}')}`,
    `${indent(1)}trivy_username: ${quote(d.trivyUsername || '${TRIVY_USERNAME}')}`,
    `${indent(1)}trivy_robot_secret: ${quote(d.trivyRobotSecret || '${TRIVY_ROBOT_SECRET}')}`,
    '',
    'departments:',
    `${indent(1)}- department_id: ${quote(deptId)}`,
    `${indent(2)}name: ""`,
    `${indent(2)}representor: ""`,
    `${indent(2)}representor_email: ""`,
    '',
    `${indent(2)}cocs:`,
    `${indent(3)}- coc_id: ${quote(cocId)}`,
    `${indent(4)}department_id: ${quote(deptId)}`,
    `${indent(4)}name: ""`,
    `${indent(4)}representor: ""`,
    `${indent(4)}representor_email: ""`,
    '',
    `${indent(4)}projects:`,
    `${indent(5)}- department_id: ${quote(deptId)}`,
    `${indent(6)}coc_id: ${quote(cocId)}`,
    `${indent(6)}project_name: ${quote(d.projectName)}`,
    `${indent(6)}lean_ix_id: ${quote(d.leanIxId)}`,
    `${indent(6)}project_phase: ${quote(normalizePhase(d.projectPhase))}`,
    `${indent(6)}manager: ""`,
    `${indent(6)}manager_email: ""`,
    `${indent(6)}spoc:`,
  ];

  const validSpoc = d.spocs.find((s) => s.name && s.email);
  if (validSpoc) {
    lines.push(
      `${indent(7)}name: ${quote(validSpoc.name)}`,
      `${indent(7)}email: ${quote(validSpoc.email)}`,
    );
  }

  lines.push(
    `${indent(6)}classification:`,
    `${indent(7)}criticality: ${quote(d.criticality)}`,
    `${indent(7)}data_classification: ${quote(d.dataClassification)}`,
    `${indent(7)}compliance_tags:`,
    ...(d.complianceTags.length > 0
      ? d.complianceTags.map((tag) => `${indent(8)}- ${quote(tag)}`)
      : [`${indent(8)}- "ISO-27001"`]),
    `${indent(6)}connections:`,
  );

  if (d.gheRepos.some((r) => r.url)) {
    lines.push(
      `${indent(7)}ghe:`,
      `${indent(8)}repos:`,
    );
    d.gheRepos.filter((r) => r.url).forEach((r) => {
      lines.push(
        `${indent(9)}- url: ${quote(r.url)}`,
        `${indent(10)}branch: ${quote(r.branch || 'main')}`,
      );
      if (r.criticality) lines.push(`${indent(10)}repo_criticality: ${quote(r.criticality)}`);
      if (r.type) lines.push(`${indent(10)}repo_type: ${quote(r.type)}`);
      const langs = r.languages && r.languages.length > 0 ? r.languages : d.languages;
      const fws = r.frameworks && r.frameworks.length > 0 ? r.frameworks : d.frameworks;
      const bts = r.buildTools && r.buildTools.length > 0 ? r.buildTools : d.buildTools;
      if (langs.length > 0 || fws.length > 0 || bts.length > 0) {
        lines.push(`${indent(10)}tech_stack:`);
        if (langs.length > 0) lines.push(`${indent(11)}languages: ${listStr(langs)}`);
        if (fws.length > 0) lines.push(`${indent(11)}frameworks: ${listStr(fws)}`);
        if (bts.length > 0) lines.push(`${indent(11)}build_tools: ${listStr(bts)}`);
      }
    });
  }

  if (d.sonarUrl || d.sonarOrg) {
    const sonarKeys = d.gheRepos.map((r) => getRepoNameFromUrl(r.url)).filter(Boolean);
    lines.push(
      `${indent(7)}sonar:`,
      `${indent(8)}url: ${quote(d.sonarUrl)}`,
      `${indent(8)}org: ${quote(d.sonarOrg)}`,
      `${indent(8)}project_keys:`,
    );
    (sonarKeys.length > 0 ? sonarKeys : ['']).forEach((k) => { if (k) lines.push(`${indent(9)}- ${quote(k)}`); });
  }

  if (d.blackduckUrl) {
    const bdKeys = d.gheRepos.map((r) => getRepoNameFromUrl(r.url)).filter(Boolean);
    lines.push(
      `${indent(7)}blackduck:`,
      `${indent(8)}url: ${quote(d.blackduckUrl)}`,
      `${indent(8)}project_keys:`,
    );
    (bdKeys.length > 0 ? bdKeys : ['']).forEach((k) => { if (k) lines.push(`${indent(9)}- ${quote(k)}`); });
  }

  // Polaris — only the application UUID is written. No credential is ever included:
  // Sentinel authenticates with one centrally managed service-account token.
  if (d.polarisApplicationId) {
    lines.push(
      `${indent(7)}polaris:`,
      `${indent(8)}application_id: ${quote(d.polarisApplicationId)}`,
    );
  }

  const derivedImages = d.gheRepos
    .map((r) => { const m = r.url.match(/\/([^/]+?)(?:\.git)?$/); const name = m ? m[1] : ''; if (!name) return ''; const tag = r.branch.trim() || 'latest'; return `${name}:${tag}`; })
    .filter(Boolean);
  if (d.containerRegistry || derivedImages.length > 0) {
    const severities = d.containerSeverityThreshold.length > 0 ? d.containerSeverityThreshold : ['BLOCKER', 'CRITICAL'];
    lines.push(
      `${indent(7)}container_scan:`,
      `${indent(8)}registry: ${quote(d.containerRegistry)}`,
    );
    if (d.containerProject) lines.push(`${indent(8)}project: ${quote(d.containerProject)}`);
    lines.push(
      `${indent(8)}severity_threshold:`,
      ...severities.map((s) => `${indent(9)}- ${s}`),
      `${indent(8)}scan_schedule: ${quote(d.containerScanSchedule || 'on_push')}`,
      `${indent(8)}images:`,
    );
    derivedImages.forEach((img) => { lines.push(`${indent(9)}- ${quote(img)}`); });
  }

  if (d.gitleaksEnabled) {
    lines.push(
      `${indent(7)}gitleaks:`,
      `${indent(8)}enabled: true`,
      `${indent(8)}config_path: ${quote(d.gitleaksConfigPath || '.gitleaks.toml')}`,
    );
  }

  const projLangs = d.languages.length > 0 ? d.languages
    : (d.gheRepos.flatMap((r) => r.languages || []).filter(Boolean).length > 0
      ? [...new Set(d.gheRepos.flatMap((r) => r.languages || []))]
      : ['Java']);
  const projFws = d.frameworks.length > 0 ? d.frameworks
    : (d.gheRepos.flatMap((r) => r.frameworks || []).filter(Boolean).length > 0
      ? [...new Set(d.gheRepos.flatMap((r) => r.frameworks || []))]
      : ['Spring Boot']);
  const projBts = d.buildTools.length > 0 ? d.buildTools
    : (d.gheRepos.flatMap((r) => r.buildTools || []).filter(Boolean).length > 0
      ? [...new Set(d.gheRepos.flatMap((r) => r.buildTools || []))]
      : ['Gradle']);
  lines.push(
    `${indent(6)}tech_stack:`,
    `${indent(7)}languages: ${listStr(projLangs)}`,
    `${indent(7)}frameworks: ${listStr(projFws)}`,
    `${indent(7)}build_tools: ${listStr(projBts)}`,
  );

  const notifEmails = deriveNotificationEmails(d);
  if (notifEmails.length > 0) {
    lines.push(
      `${indent(6)}notifications:`,
      `${indent(7)}channels:`,
      `${indent(8)}- type: "email"`,
      `${indent(9)}recipients:`,
      ...notifEmails.map((e) => `${indent(10)}- ${quote(e)}`),
      `${indent(9)}alert_types:`,
      ...d.alertTypes.map((a) => `${indent(10)}- ${quote(a)}`),
    );
  }

  return lines.join('\n') + '\n';
}

// ── Reverse parser: YAML string → OnboardingFormData ──

function extractYamlValue(yaml: string, path: string): string {
  const escaped = path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const m = yaml.match(new RegExp(`${escaped}:\\s*"?([^"\\n]*)"?`));
  return m?.[1]?.trim() || '';
}

function extractYamlList(yaml: string, path: string): string[] {
  const escaped = path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const m = yaml.match(new RegExp(`${escaped}:\\s*\\n((?:\\s*-\\s*.+\\n?)*)`, 'm'));
  if (!m?.[1]) {
    const inl = yaml.match(new RegExp(`${escaped}:\\s*\\[([^\\]]+)\\]`));
    if (inl?.[1]) return inl[1].split(',').map((s) => s.trim().replace(/^["']|["']$/g, '')).filter(Boolean);
    return [];
  }
  return m[1].split('\n').map((l) => l.match(/^\s*-\s*"?([^"]*)"?$/)?.[1]?.trim() || '').filter(Boolean);
}

export function parseYamlToForm(yaml: string): OnboardingFormData {
  const form = { ...EMPTY_FORM };

  form.ghePat = extractYamlValue(yaml, 'ghe_pat');
  form.sonarToken = extractYamlValue(yaml, 'sonar_token');
  form.blackduckApiToken = extractYamlValue(yaml, 'blackduck_api_token');
  form.trivyUsername = extractYamlValue(yaml, 'trivy_username');
  form.trivyRobotSecret = extractYamlValue(yaml, 'trivy_robot_secret');

  form.projectName = extractYamlValue(yaml, 'project_name');
  form.leanIxId = extractYamlValue(yaml, 'lean_ix_id');
  form.projectPhase = normalizePhase(extractYamlValue(yaml, 'project_phase'));

  const spocBlock = yaml.match(/spoc:\s*\n((?:\s+\S.*\n?)*)/)?.[1] || '';
  const spocName = extractYamlValue(spocBlock, 'name');
  const spocEmail = extractYamlValue(spocBlock, 'email');
  if (spocName || spocEmail) {
    form.spocs = [{ name: spocName || '', email: spocEmail || '' }];
  }

  form.criticality = extractYamlValue(yaml, 'criticality') || 'MEDIUM';
  form.dataClassification = extractYamlValue(yaml, 'data_classification') || 'INTERNAL';
  form.complianceTags = extractYamlList(yaml, 'compliance_tags');

  const gheSection = yaml.match(/ghe:\s*\n([\s\S]*?)(?=\n {14}\S|$)/)?.[0] || '';
  if (gheSection) {
    const urlRe = /- url:/g;
    const positions: number[] = [];
    let urlM: RegExpExecArray | null;
    while ((urlM = urlRe.exec(gheSection)) !== null) positions.push(urlM.index);

    if (positions.length > 0) {
      const repoChunks = positions.map((start, i) =>
        gheSection.slice(start, i + 1 < positions.length ? positions[i + 1] : gheSection.length),
      );
      form.gheRepos = repoChunks.map((chunk) => ({
        url: extractYamlValue(chunk, 'url'),
        branch: extractYamlValue(chunk, 'branch') || 'main',
        languages: extractYamlList(chunk, 'languages'),
        frameworks: extractYamlList(chunk, 'frameworks'),
        buildTools: extractYamlList(chunk, 'build_tools'),
        criticality: (extractYamlValue(chunk, 'repo_criticality').toUpperCase() || '') as GheRepo['criticality'],
        type: (extractYamlValue(chunk, 'repo_type').toUpperCase() || '') as GheRepo['type'],
      }));
    }
  }

  const sonarSection = yaml.match(/sonar:\s*\n([\s\S]*?)(?=\n\s{14}\w|$)/)?.[0] || '';
  if (sonarSection) {
    form.sonarUrl = extractYamlValue(sonarSection, 'url');
    form.sonarOrg = extractYamlValue(sonarSection, 'org');
  }

  const bdSection = yaml.match(/blackduck:\s*\n([\s\S]*?)(?=\n\s{14}\w|$)/)?.[0] || '';
  if (bdSection) {
    form.blackduckUrl = extractYamlValue(bdSection, 'url');
  }

  const polarisSection = yaml.match(/polaris:\s*\n([\s\S]*?)(?=\n\s{14}\w|$)/)?.[0] || '';
  if (polarisSection) {
    form.polarisApplicationId = extractYamlValue(polarisSection, 'application_id');
  }

  form.containerRegistry = extractYamlValue(yaml, 'registry');
  form.containerProject = extractYamlValue(yaml, 'project');
  // If project is empty but registry URL embeds a project path (e.g. ".../projects/nerd/..."), extract it
  if (!form.containerProject && form.containerRegistry) {
    const projMatch = form.containerRegistry.match(/\/projects\/([^/]+)/);
    if (projMatch) {
      form.containerProject = projMatch[1];
      // Also clean the registry to just the hostname
      form.containerRegistry = form.containerRegistry.replace(/^https?:\/\//, '').split('/')[0];
    }
  }
  const severityList = extractYamlList(yaml, 'severity_threshold');
  form.containerSeverityThreshold = severityList.length > 0 ? severityList : ['BLOCKER', 'CRITICAL'];
  form.containerScanSchedule = extractYamlValue(yaml, 'scan_schedule') || 'on_push';
  form.containerImages = extractYamlList(yaml, 'images');

  const gitleaksEnabled = extractYamlValue(yaml, 'enabled');
  form.gitleaksEnabled = gitleaksEnabled === 'true' || gitleaksEnabled === '';
  form.gitleaksConfigPath = extractYamlValue(yaml, 'config_path') || '.gitleaks.toml';

  const projTechBlock = yaml.match(/^ {12}tech_stack:\s*\n((?:^ {14}[^\n]+\n?)*)/m)?.[0] || '';
  if (projTechBlock) {
    const langs = extractYamlList(projTechBlock, 'languages');
    const fws = extractYamlList(projTechBlock, 'frameworks');
    const bts = extractYamlList(projTechBlock, 'build_tools');
    if (langs.length > 0) form.languages = langs;
    if (fws.length > 0) form.frameworks = fws;
    if (bts.length > 0) form.buildTools = bts;
  } else {
    form.languages = extractYamlList(yaml, 'languages');
    form.frameworks = extractYamlList(yaml, 'frameworks');
    form.buildTools = extractYamlList(yaml, 'build_tools');
  }

  return form;
}

export function deriveConfigNameFromYaml(yaml: string): string {
  const name = extractYamlValue(yaml, 'project_name');
  return name
    ? name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'unnamed-config'
    : 'unnamed-config';
}

// ── Pipeline YAML generators (ported from ai4swe-secops-app, based on NERD GitHub Actions templates) ──

interface PipelineContext {
  repoName: string;
  repoUrl: string;
  branch: string;
  sonarProjectKey: string;
  blackduckProjectName: string;
  languages: string[];
  buildTools: string[];
  containerRegistry: string;
}

function derivePipelineCtx(d: OnboardingFormData, repoIdx: number): PipelineContext {
  const repo = d.gheRepos[repoIdx] || { url: '', branch: 'main', languages: [], frameworks: [], buildTools: [], criticality: '', type: '' };
  const urlMatch = repo.url.match(/\/([^/]+?)(?:\.git)?$/);
  const repoName = urlMatch?.[1] || 'my-service';
  const sonarKey = getRepoNameFromUrl(repo.url) || repoName;
  const langs = (repo.languages && repo.languages.length > 0) ? repo.languages
    : (d.languages.length > 0 ? d.languages : ['Java']);
  const tools = (repo.buildTools && repo.buildTools.length > 0) ? repo.buildTools
    : (d.buildTools.length > 0 ? d.buildTools : ['Gradle']);
  return {
    repoName,
    repoUrl: repo.url,
    branch: repo.branch || 'main',
    sonarProjectKey: sonarKey,
    blackduckProjectName: repoName,
    languages: langs,
    buildTools: tools,
    containerRegistry: d.containerRegistry,
  };
}

function isJavaProject(ctx: PipelineContext): boolean {
  return ctx.languages.some((l) => /java|kotlin|scala/i.test(l));
}

function buildStep(ctx: PipelineContext): string {
  if (isJavaProject(ctx)) {
    const tool = ctx.buildTools.some((t) => /maven/i.test(t)) ? 'mvn' : './gradlew';
    const permLine = tool === './gradlew' ? `      - name: Change Wrapper Permissions\n        run: chmod +x ./gradlew\n` : '';
    return `${permLine}      - name: Build\n        env:\n          ARTIFACTORY_BASE_URL: \${{ secrets.ARTIFACTORY_BASE_URL }}\n          ARTIFACTORY_USERNAME: \${{ secrets.ARTIFACTORY_USERNAME }}\n          ARTIFACTORY_PASSWORD: \${{ secrets.ARTIFACTORY_PASSWORD }}\n        run: ${tool} clean build -x test`;
  }
  return `      - name: Install & Build\n        run: |\n          npm ci\n          npm run build`;
}

export function generateCiYaml(d: OnboardingFormData, repoIdx: number): string {
  const ctx = derivePipelineCtx(d, repoIdx);
  const javaSetup = isJavaProject(ctx)
    ? `      - name: Java Installation\n        uses: actions/setup-java@v5.2.0\n        with:\n          distribution: 'temurin'\n          java-version: '21'\n`
    : `      - name: Node.js Setup\n        uses: actions/setup-node@v4\n        with:\n          node-version: '20'\n`;

  const dockerTag = `registry-emea.app.corpintra.net/nerd/dev/${ctx.repoName}:${ctx.repoName}_B\${{ github.run_number }}_dev`;

  return `name: CI Build Image — ${ctx.repoName}

on:
  workflow_dispatch:
  push:
    branches: [ 'feature/**' ]

jobs:
  branch_check:
    name: Branch Check
    runs-on: ubuntu-2core-amd64
    steps:
      - name: Checkout branch - \${{ github.ref_name }}
        uses: actions/checkout@v6.0.2
      - name: Branch Validation
        run: |
          branch="\${{ github.ref_name }}"
          if [[ "$branch" == master || "$branch" == uat || "$branch" == develop ]]; then
            echo "Manual build is restricted on the $branch branch"
            exit 1
          fi

  build_and_push:
    name: Build & Push Image
    runs-on: ubuntu-2core-amd64
    needs: branch_check
    steps:
      - name: Checkout Source Code
        uses: actions/checkout@v6.0.2
        with:
          fetch-depth: 0
${javaSetup}${buildStep(ctx)}
      - name: Set Up Docker Buildx
        uses: docker/setup-buildx-action@v4.1.0
      - name: Login to Harbor
        uses: docker/login-action@v4.2.0
        with:
          registry: registry-emea.app.corpintra.net
          username: \${{ secrets.DOCKERHUBREG_USERNAME }}
          password: \${{ secrets.DOCKERHUBREG_TOKEN }}
      - name: Build and Push Image
        uses: docker/build-push-action@v7.2.0
        with:
          context: .
          file: ./Dockerfile
          push: true
          tags: ${dockerTag}
      - name: Summary
        run: |
          echo ':herb: Branch → \${{ github.ref_name }}' >> $GITHUB_STEP_SUMMARY
          echo ':whale: Image → ${dockerTag}' >> $GITHUB_STEP_SUMMARY
          echo ':technologist: Executor → \${{ github.actor }}' >> $GITHUB_STEP_SUMMARY

  sonarqube_scan:
    name: SonarQube Scan
    runs-on: ubuntu-2core-amd64
    needs: build_and_push
    steps:
      - name: Checkout code
        uses: actions/checkout@v6.0.2
        with:
          fetch-depth: 0
      - name: Sonar Scan
        uses: SonarSource/sonarqube-scan-action@v8.1.0
        env:
          SONAR_TOKEN: \${{ secrets.SONAR_TOKEN }}
          SONAR_HOST_URL: \${{ secrets.SONAR_HOST_URL }}
        with:
          args: >
            -Dsonar.projectKey=${ctx.sonarProjectKey}
            -Dsonar.verbose=true
            -Dsonar.sources=src
            -Dsonar.branch.name=\${{ github.ref_name }}

  blackduck_scan:
    name: Black Duck Scan
    runs-on: ubuntu-2core-amd64
    needs: build_and_push
    steps:
      - name: Checkout Source Code
        uses: actions/checkout@v6.0.2
        with:
          fetch-depth: 0
      - name: Black Duck SCA Scan
        uses: blackduck-inc/black-duck-security-scan@v2.9.0
        env:
          DETECT_PROJECT_NAME: ${ctx.blackduckProjectName}
          DETECT_PROJECT_VERSION_NAME: \${{ github.ref_name }}
          DETECT_TOOLS: DETECTOR
        with:
          blackducksca_url: \${{ secrets.BLACKDUCK_HOST_URL }}
          blackducksca_token: \${{ secrets.BLACKDUCK_API_TOKEN }}
          blackducksca_scan_full: true
          blackducksca_scan_failure_severities: BLOCKER,CRITICAL
`;
}

export function generateCicdYaml(d: OnboardingFormData, repoIdx: number): string {
  const ctx = derivePipelineCtx(d, repoIdx);
  const javaSetup = isJavaProject(ctx)
    ? `      - name: Java Installation\n        uses: actions/setup-java@v5.2.0\n        with:\n          distribution: 'temurin'\n          java-version: '21'\n`
    : `      - name: Node.js Setup\n        uses: actions/setup-node@v4\n        with:\n          node-version: '20'\n`;

  return `name: CICD — ${ctx.repoName}

on:
  pull_request:
    types: [ closed ]
    branches: [ develop, uat, master ]

jobs:
  commit_check:
    name: Commit Check
    if: github.event.pull_request.merged == true
    runs-on: ubuntu-2core-amd64
    env:
      GH_TOKEN: \${{ secrets.PERSONAL_ACCESS_TOKEN }}
    steps:
      - name: Checkout Source
        uses: actions/checkout@v6.0.2
        with:
          fetch-depth: 0
          ref: \${{ github.ref_name }}
          token: \${{ env.GH_TOKEN }}
      - name: Check commit message
        run: |
          merge_commit_message=$(git log --format=%B -n 1 \${{ github.event.pull_request.merge_commit_sha }})
          echo "$merge_commit_message"
          if [[ "$merge_commit_message" != *"Merge pull request"* ]]; then
            echo "Merge commit message does not contain 'Merge pull request'. Exiting."
            exit 1
          fi

  environment:
    name: Determine Environment
    runs-on: ubuntu-2core-amd64
    needs: commit_check
    outputs:
      env_name: \${{ env.env_name }}
      REPO: \${{ env.REPO }}
    steps:
      - name: Set environment
        run: |
          if [ "\${{ github.ref_name }}" = "master" ]; then
            echo "env_name=prod" >> $GITHUB_ENV
            echo "REPO=prod" >> $GITHUB_ENV
          elif [ "\${{ github.ref_name }}" = "uat" ]; then
            echo "env_name=uat" >> $GITHUB_ENV
            echo "REPO=uat" >> $GITHUB_ENV
          elif [ "\${{ github.ref_name }}" = "develop" ]; then
            echo "env_name=int" >> $GITHUB_ENV
            echo "REPO=int" >> $GITHUB_ENV
          else
            echo "env_name=feature" >> $GITHUB_ENV
            echo "REPO=dev" >> $GITHUB_ENV
          fi

  sonarqube_scan:
    name: SonarQube Scan
    runs-on: ubuntu-2core-amd64
    needs: [ commit_check, environment ]
    steps:
${javaSetup}      - name: Checkout Source Code
        uses: actions/checkout@v6.0.2
        with:
          fetch-depth: 0
          ref: \${{ github.ref_name }}
${buildStep(ctx)}
      - name: Sonar Scan
        uses: SonarSource/sonarqube-scan-action@v8.1.0
        env:
          SONAR_TOKEN: \${{ secrets.SONAR_TOKEN }}
          SONAR_HOST_URL: \${{ secrets.SONAR_HOST_URL }}
        with:
          args: >
            -Dsonar.projectKey=${ctx.sonarProjectKey}
            -Dsonar.verbose=true
            -Dsonar.sources=src
            -Dsonar.branch.name=\${{ github.ref_name }}

  blackduck_scan:
    name: Black Duck Scan
    runs-on: ubuntu-2core-amd64
    needs: [ commit_check, environment ]
    steps:
${javaSetup}      - name: Checkout Source Code
        uses: actions/checkout@v6.0.2
        with:
          fetch-depth: 0
          ref: \${{ github.ref_name }}
${buildStep(ctx)}
      - name: Black Duck SCA Scan
        uses: blackduck-inc/black-duck-security-scan@v2.9.0
        env:
          DETECT_PROJECT_NAME: ${ctx.blackduckProjectName}
          DETECT_PROJECT_VERSION_NAME: \${{ github.ref_name }}
          DETECT_TOOLS: DETECTOR
        with:
          blackducksca_url: \${{ secrets.BLACKDUCK_HOST_URL }}
          blackducksca_token: \${{ secrets.BLACKDUCK_API_TOKEN }}
          blackducksca_scan_full: true
          blackducksca_scan_failure_severities: BLOCKER,CRITICAL

  image_tag:
    name: Image Tag
    runs-on: ubuntu-2core-amd64
    needs: [ environment, sonarqube_scan, blackduck_scan ]
    outputs:
      fimg_tag: \${{ env.fimg_tag }}
    steps:
      - name: Generate tag
        run: echo "fimg_tag=${ctx.repoName}-\${{ needs.environment.outputs.env_name }}_B\${{ github.run_number }}_$(date +'%m-%d-%y')" >> $GITHUB_ENV

  docker_build_push:
    name: Docker Build & Push
    needs: [ environment, image_tag ]
    runs-on: ubuntu-2core-amd64
    steps:
      - name: Checkout Source Code
        uses: actions/checkout@v6.0.2
        with:
          ref: \${{ github.ref_name }}
          fetch-depth: 0
${javaSetup}${buildStep(ctx)}
      - name: Set Up Docker Buildx
        uses: docker/setup-buildx-action@v4.1.0
      - name: Login to Harbor
        uses: docker/login-action@v4.2.0
        with:
          registry: registry-emea.app.corpintra.net
          username: \${{ secrets.DOCKERHUBREG_USERNAME }}
          password: \${{ secrets.DOCKERHUBREG_TOKEN }}
      - name: Build and Push Image
        uses: docker/build-push-action@v7.2.0
        with:
          context: .
          file: ./Dockerfile
          push: true
          tags: registry-emea.app.corpintra.net/nerd/\${{ needs.environment.outputs.REPO }}/${ctx.repoName}:\${{ needs.image_tag.outputs.fimg_tag }}

  deployment:
    name: CLIMB Deployment
    needs: [ environment, image_tag, docker_build_push ]
    runs-on: ubuntu-2core-amd64
    steps:
      - name: Deploy via CLIMB
        run: |
          curl -sSL https://facets-cloud.github.io/facets-schemas/scripts/fctl_install.sh | bash && source ~/.bashrc
          export DOCKER_IMAGE_URL=registry-emea.app.corpintra.net/nerd/\${{ needs.environment.outputs.REPO }}/${ctx.repoName}:\${{ needs.image_tag.outputs.fimg_tag }}
          export TOKEN=\${{ secrets.CLIMB_TOKEN }}
          export GIT_REF=\${{ github.ref_name }}
          curl -s https://facets-cloud.github.io/facets-schemas/scripts/fctl_register.sh | bash -s -- -c climb.i.mercedes-benz.com -p nerd -s ${ctx.repoName} -a nerd-harbor -i "false"
`;
}

export function generatePrYaml(d: OnboardingFormData, repoIdx: number): string {
  const ctx = derivePipelineCtx(d, repoIdx);
  const javaSetup = isJavaProject(ctx)
    ? `      - name: Java Installation\n        uses: actions/setup-java@v5.2.0\n        with:\n          distribution: 'temurin'\n          java-version: '21'\n`
    : `      - name: Node.js Setup\n        uses: actions/setup-node@v4\n        with:\n          node-version: '20'\n`;

  return `name: PR Checks — ${ctx.repoName}

on:
  pull_request:
    branches: [ develop, uat, master ]

jobs:
  jira_ticket_check:
    name: JIRA Ticket Integration
    runs-on: ubuntu-2core-amd64
    steps:
      - name: Checkout branch
        uses: actions/checkout@v6.0.2
        with:
          fetch-depth: 0
      - name: Validate commit contains JIRA ticket
        run: |
          latest_msg=$(git log --no-merges -n 1 --pretty=format:"%s")
          if [ -z "$latest_msg" ]; then
            echo "No non-merge commits found; passing check."
            exit 0
          fi
          echo "Latest commit: $latest_msg"
          if echo "$latest_msg" | grep -Eq 'NERD-[0-9]+'; then
            echo "JIRA ticket validation passed."
          else
            echo "JIRA ticket not associated. Commit must contain a NERD ticket (e.g. NERD-123)."
            exit 1
          fi

  sonarqube_scan:
    name: SonarQube Scan
    runs-on: ubuntu-2core-amd64
    needs: jira_ticket_check
    steps:
${javaSetup}      - name: Checkout Source Code
        uses: actions/checkout@v6.0.2
        with:
          fetch-depth: 0
${buildStep(ctx)}
      - name: SonarQube Scan
        env:
          SONAR_TOKEN: \${{ secrets.SONAR_TOKEN }}
          SONAR_HOST_URL: \${{ secrets.SONAR_HOST_URL }}
        run: |
          # PR decoration scan
          echo "Scanning PR \${{ github.event.number }} from \${{ github.head_ref }} to \${{ github.base_ref }}"

  blackduck_scan:
    name: Black Duck Scan
    runs-on: ubuntu-2core-amd64
    needs: jira_ticket_check
    steps:
${javaSetup}      - name: Checkout Source Code
        uses: actions/checkout@v6.0.2
        with:
          fetch-depth: 0
          ref: \${{ github.head_ref }}
${buildStep(ctx)}
      - name: Black Duck SCA Scan
        uses: blackduck-inc/black-duck-security-scan@v2.9.0
        env:
          DETECT_PROJECT_NAME: ${ctx.blackduckProjectName}
          DETECT_PROJECT_VERSION_NAME: \${{ github.head_ref }}
          DETECT_TOOLS: DETECTOR
        with:
          blackducksca_url: \${{ secrets.BLACKDUCK_HOST_URL }}
          blackducksca_token: \${{ secrets.BLACKDUCK_API_TOKEN }}
          blackducksca_scan_full: false
          blackducksca_scan_failure_severities: BLOCKER,CRITICAL
          mark_build_status: success
`;
}
